import React from 'react';
import { ResourcePostData } from '../types';

export const fivePitchDeckMistakes: ResourcePostData = {
    id: "5-pitch-deck-mistakes-kill-fundraising",
    series: "Fundraising 101",
    title: "5 Pitch Deck Mistakes That Kill Fundraising (2026 Guide)",
    date: "Oct 27, 2025",
    readTime: "7 min read",
    tags: ["Pitch Deck", "Mistakes", "Fundraising"],
    sections: [
        {
            id: "introduction",
            title: "Introduction",
            content: (
                <>
                    <p>
                        When founders say investors aren’t responding, the problem usually isn’t the idea — it’s the pitch. A pitch deck is often the first filter investors use to judge whether you’re worth a meeting. And certain mistakes instantly kill your chances.
                    </p>
                    <p>
                        Here are the five pitch deck mistakes that destroy fundraising outcomes, and how to avoid them in 2025.
                    </p>
                </>
            )
        },
        {
            id: "mistake-1",
            title: "1. An Unclear Problem Statement",
            content: (
                <>
                    <p>Your pitch dies the moment the investor can’t understand the problem.</p>
                    <p className="mt-2 mb-1"><strong>Most founders do one of these:</strong></p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Describe the problem in vague language</li>
                        <li>Add too much context</li>
                        <li>Make the problem sound weak</li>
                        <li>Use buzzwords instead of real user pain</li>
                    </ul>
                    <div className="bg-red-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-red-800"><strong>Why it kills fundraising:</strong> If the problem isn’t sharp, the rest of the story collapses.</p>
                    </div>
                    <p className="mt-4 mb-1"><strong>Fix it:</strong> Use this formula:</p>
                    <p className="font-medium">User → Pain → Cost → Why now</p>
                    <div className="bg-green-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-green-800"><strong>Example:</strong> “Small retailers lose 12–15% of revenue every month due to offline inventory errors. Legacy tools are built for enterprises, not them.”</p>
                        <p className="text-sm text-green-800 mt-1">Clear. Sharp. Painful.</p>
                    </div>
                </>
            )
        },
        {
            id: "mistake-2",
            title: "2. Overcrowded Slides (Too Much Text)",
            content: (
                <>
                    <p>The fastest way to lose investor attention is a slide packed with text.</p>
                    <p className="mt-2 mb-1"><strong>Signs of overcrowded slides:</strong></p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Paragraphs instead of bullets</li>
                        <li>Heavy explanations</li>
                        <li>Multiple ideas on one slide</li>
                        <li>No white space</li>
                        <li>Conflicting fonts and layouts</li>
                    </ul>
                    <div className="bg-red-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-red-800"><strong>Why it kills fundraising:</strong> Investors skim. If your slide requires reading… they skip. And once skipped, your narrative breaks.</p>
                    </div>
                    <p className="mt-4 mb-1"><strong>Fix it:</strong> Use the 3×3 rule:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Max 3 bullet points</li>
                        <li>Max 3 lines per bullet</li>
                    </ul>
                    <p className="mt-2 italic">Clean visuals = stronger storytelling.</p>
                </>
            )
        },
        {
            id: "mistake-3",
            title: "3. Unrealistic Market Size (TAM Lies)",
            content: (
                <>
                    <p>Founders love showing big TAM numbers like $100B market opportunity. Investors hate it.</p>
                    <p className="mt-2 mb-1"><strong>Signs your TAM looks fake:</strong></p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>You chose the largest possible number</li>
                        <li>You didn’t explain how you calculated TAM</li>
                        <li>Your SAM/SOM has no logic</li>
                        <li>You used random Google stats</li>
                    </ul>
                    <div className="bg-red-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-red-800"><strong>Why it kills fundraising:</strong> Unrealistic TAM makes you look inexperienced or dishonest.</p>
                    </div>
                    <p className="mt-4 mb-1"><strong>Fix it:</strong> Show:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>TAM</strong> — broad</li>
                        <li><strong>SAM</strong> — relevant</li>
                        <li><strong>SOM</strong> — achievable</li>
                    </ul>
                    <p className="mt-2">This one slide can determine whether you are taken seriously.</p>
                </>
            )
        },
        {
            id: "mistake-4",
            title: "4. Weak or Missing Traction",
            content: (
                <>
                    <p>Traction is the single most important slide in any pitch deck. But founders often:</p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                        <li>Put vanity metrics</li>
                        <li>Hide weak numbers</li>
                        <li>Add irrelevant data</li>
                        <li>Skip traction entirely</li>
                    </ul>
                    <div className="bg-red-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-red-800"><strong>Why it kills fundraising:</strong> Investors assume no traction = no real validation.</p>
                    </div>
                    <p className="mt-4 mb-1"><strong>Fix it:</strong> Show ANY of the following:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Paying users</li>
                        <li>Revenue</li>
                        <li>MRR growth</li>
                        <li>Pilot results</li>
                        <li>Early conversions</li>
                        <li>Waitlist size</li>
                        <li>Engagement</li>
                    </ul>
                    <p className="mt-2 italic">Even small traction &gt; no traction.</p>
                </>
            )
        },
        {
            id: "mistake-5",
            title: "5. No Clear Funding Ask",
            content: (
                <>
                    <p>The pitch ends… and there’s no funding ask. Or worse — the ask is vague.</p>
                    <p className="mt-2 mb-1"><strong>Founders often write:</strong></p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>“We are raising funds to grow.”</li>
                        <li>“We need capital to expand.”</li>
                        <li>“Looking for smart money.”</li>
                    </ul>
                    <p className="mt-2 text-red-600 font-medium">This is useless.</p>
                    <div className="bg-red-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-red-800"><strong>Why it kills fundraising:</strong> Investors need clarity. If you can’t define what you need, they assume you aren’t prepared.</p>
                    </div>
                    <p className="mt-4 mb-1"><strong>Fix it:</strong> Use this structure:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Amount</li>
                        <li>Use of funds (3–5 buckets)</li>
                        <li>Runway</li>
                        <li>Milestones</li>
                    </ul>
                    <div className="bg-green-50 p-4 rounded-lg mt-4">
                        <p className="text-sm text-green-800"><strong>Example:</strong> “Raising $500K for 18 months of runway to expand GTM, strengthen product, and build early leadership.”</p>
                        <p className="text-sm text-green-800 mt-1">Clear founder → confident founder.</p>
                    </div>
                </>
            )
        },
        {
            id: "bonus-mistake",
            title: "Bonus: The Hidden Pitch Deck Mistake Most Founders Ignore",
            content: (
                <>
                    <p><strong>Your story doesn’t flow.</strong></p>
                    <p>Even if each slide is good, if the narrative feels disconnected, investors mentally check out.</p>
                    <p className="mt-2">A pitch deck is not a collection of slides. It’s a structured story.</p>
                    <p className="mt-4 mb-1"><strong>The best decks follow a predictable, logical flow:</strong></p>
                    <p className="bg-gray-100 p-3 rounded text-sm text-center">Problem → Solution → Market → Product → Traction → GTM → Business Model → Competition → Financials → Team → Ask</p>
                    <p className="mt-2">This structure works because it matches how investors think.</p>
                </>
            )
        },
        {
            id: "conclusion",
            title: "Conclusion",
            content: (
                <>
                    <p>Fundraising doesn’t fail because founders lack intelligence.</p>
                    <p>It fails because their pitch decks contain avoidable, fatal mistakes.</p>
                </>
            )
        }
    ]
};
